
import { Component, Vue } from 'vue-property-decorator'
import User from '@/types/user'
import axios from 'axios'
import moment from 'moment/moment'
import { realtimeService, RealtimeUnsubscribe } from '@/services/realtime'

interface Notification {
  id: string
  title: string
  body: string
  model: string
  model_id: string
  created_at: string
  sender: string
  read_at?: string | null
  data?: Notification
}

@Component({
  components: {}
})

export default class NotificationCenter extends Vue {
  moment = moment;
  showNotificationCenter = false
  notifications:Notification[] = []
  notificationSubscription?: RealtimeUnsubscribe

  mounted () : void {
    this.connectRealtime()
    this.getNotifications()
  }

  beforeDestroy () : void {
    if (this.notificationSubscription) {
      this.notificationSubscription()
    }
  }

  connectRealtime (): void {
    const token = this.$store.getters['authStore/authToken']
    const userId = this.authUser?.id
    if (!token || !userId) {
      return
    }

    this.notificationSubscription = realtimeService.listenPrivateNotification(
      `App.Models.User.${userId}`,
      (notification: Notification) => this.addNotification(notification)
    )
  }

  addNotification (notification : Notification) : void {
    notification.read_at = null
    this.notifications = [notification, ...this.notifications]

    this.$notification.info({
      message: notification.title,
      description: notification.body,
      duration: 5,
      onClick: () => {
        this.$router.push({ path: this.getRoutePath(notification) })
        this.markAsRead(notification.id)
      }
    })
  }

  get unreadCount () : number {
    return this.notifications.filter((n) => n.read_at === null).length
  }

  getNotifications () : void {
    axios.get(process.env.VUE_APP_API_URL + '/notifications').then((res) => {
      // notifications might be in res.data, or res.data.notifications
      const notifications = res.data.notifications || res.data
      this.notifications = notifications.map((notification : Notification) => {
        return {
          ...notification.data,
          id: notification.id,
          created_at: notification.created_at,
          read_at: notification.read_at
        }
      })
    })
  }

  markAsRead (id:string) : void {
    axios.get(process.env.VUE_APP_API_URL + '/notifications/' + id + '/mark').then(() => {
      this.getNotifications()
    })
  }

  markAllAsRead () : void {
    axios.get(process.env.VUE_APP_API_URL + '/notifications/read').then(() => {
      this.getNotifications()
    })
  }

  deleteNotification (id: string) : void {
    axios.delete(process.env.VUE_APP_API_URL + '/notifications/' + id).then(() => {
      this.getNotifications()
    })
  }

  deleteAllNotifications () : void {
    axios.delete(process.env.VUE_APP_API_URL + '/notifications').then(() => {
      this.getNotifications()
    })
  }

  toggleNotificationCenter () : void {
    this.showNotificationCenter = !this.showNotificationCenter
  }

  getRoutePath (notification: Notification): string {
    let route = ''

    switch (notification.model) {
      case 'issue':
        route = '/meldingen/' + notification.model_id + '/bewerken'
        break
      case 'question':
        route = '/vragen/' + notification.model_id + '/bewerken'
        break
      case 'objection':
        route = '/bezwaren/' + notification.model_id + '/bewerken'
        break
      case 'feedback':
        route = '/feedback/' + notification.model_id
        break
      case 'release':
        route = '/releases/' + notification.model_id + '/bewerken'
        break
    }

    return route
  }

  get authUser () : User {
    return this.$store.getters['authStore/authUser']
  }

  notificationClass (notification: Notification):string {
    let activeClass: string
    switch (notification.model) {
      case 'issue':
      case 'question':
      case 'objection':
        activeClass = 'wo-blue'
        break
      case 'feedback':
        activeClass = 'wo-green'
        break
      default:
        activeClass = 'wo-orange'
    }
    return `${activeClass} ${activeClass}-glow`
  }
}
